import { Button } from '@finn/design-system/atoms/button';
import { Input } from '@finn/design-system/atoms/input';
import { Skeleton } from '@finn/design-system/atoms/skeleton';
import { showToast } from '@finn/design-system/atoms/toast';
import { useMediaQuery } from '@finn/design-system/helpers/media';
import { ReferralPromoCosmicMetadata } from '@finn/ui-cosmic';
import { parseToHtml, useSession } from '@finn/ui-utils';
import { useEffect, useState } from 'react';

type ReferralPromoData = ReferralPromoCosmicMetadata & {
  inviteLink?: string;
  isLoading?: boolean;
  error?: any;
};

type Props = {
  data: ReferralPromoData;
};

export const ReferralPromoContent = ({ data }: Props) => {
  const [session] = useSession();
  const [isShareSupported, setIsShareSupported] = useState(false);
  const isMobile = useMediaQuery('md');

  useEffect(() => {
    setIsShareSupported(!!navigator.share && isMobile);
  }, [isMobile]);

  const handleShareOrCopy = async () => {
    if (!data.inviteLink) return;

    const referralMessage = data.voucher.copy_message_prefix + data.inviteLink;

    const copyMessage = () => {
      navigator.clipboard.writeText(referralMessage).then(() => {
        showToast({
          title: data.voucher.copy_success_toast,
          status: 'success',
        });
      });
    };

    if (isShareSupported) {
      try {
        await navigator.share({
          // German title for sharing dialog
          title: data.voucher.share_message_title,
          // German text for sharing dialog
          text: data.voucher.share_message_text,
          url: data.inviteLink,
        });
      } catch (error) {
        // Fall back to copy if share fails
        copyMessage();
      }
    } else {
      copyMessage();
    }
  };

  const renderInviteLinkSection = () => {
    if (session === null) {
      return (
        <div className="raw-html-global my-8 rounded-sm bg-white p-4 text-black body-16-semibold">
          {parseToHtml(data.voucher.login_required)}
        </div>
      );
    }

    if (data.isLoading || !session) {
      return (
        <div className="my-8 body-16-light">
          <Skeleton className="h-14 w-full rounded" />
        </div>
      );
    }

    if (data.error) {
      return (
        <div className="my-8 rounded-sm bg-white p-4 text-black body-16-semibold">
          {data.voucher.loading_failed}
        </div>
      );
    }

    if (data.inviteLink) {
      return (
        <div className="relative my-8">
          <Input
            value={data.inviteLink
              .replace(/^https:\/\/www\./, '')
              .replace(/\/de-DE/g, '')}
            readOnly
            className="w-full"
            endAdornment={
              <Button
                variant="ghost"
                type="button"
                size="sm"
                data-cy="voucher-apply"
                onClick={handleShareOrCopy}
              >
                {isShareSupported
                  ? data.voucher.share_label
                  : data.voucher.copy_label}
              </Button>
            }
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <h2 className="mobile-t4-semibold md:web-t4-semibold">
        {parseToHtml(data.title_inner)}
      </h2>
      {renderInviteLinkSection()}
      <div className="my-8 body-16-light [&>p]:my-1 [&>p]:body-16-light [&>ul>li]:py-2 [&>ul]:m-0 [&>ul]:px-4">
        {parseToHtml(data.description_inner)}
      </div>
    </div>
  );
};
